import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from '@mui/icons-material';
import { Button, Chip, Grid2 as Grid, Input, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { usePublishReport } from '@/admins/SmartReports/hooks/usePublishReport';
import { useUpdateReport } from '@/admins/SmartReports/hooks/useUpdateReport';
import { useUpdateReportVersion } from '@/admins/SmartReports/hooks/useUpdateReportVersion';
import { SmartReportTypeEnum } from '@/admins/SmartReports/types';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { ADMINS_SMART_REPORTS_PATH } from '@/utils/constants';
import { REPORT_TYPE_ICONS } from '../SmartReportList/constants';
import { useEditSmartReport } from './EditSmartReportContext';

const useStyles = makeStyles(theme => {
  const commonButtonStyles = {
    minWidth: '160px',
    fontSize: '1rem',
    borderRadius: '2em',
    padding: '0.5em 2em',
    textTransform: 'none',
    maxHeight: 'fit-content',
    boxShadow: 'none',
  };

  return {
    reportNameInput: {
      width: '100%',
      fontSize: '2rem',
      fontWeight: 'bold',
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',

      '&:hover': {
        cursor: 'text',
      },
    },

    reportName: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: theme.palette.baseColors.deepSeaNavy.c75,

      '& > svg': {
        marginLeft: '0.5em',
        display: 'inline-block',
        opacity: 0,
        transition: 'opacity 0.3s',
      },

      '&:hover': {
        cursor: 'pointer',

        '& > svg': {
          opacity: 1,
        },
      },
    },
    button: {
      ...commonButtonStyles,
    },
    saveButton: {
      ...commonButtonStyles,
      backgroundColor: theme.palette.baseColors.deepSeaNavy.c25,
    },
    backButton: {
      display: 'flex',
      textDecoration: 'none',
      width: 'fit-content',
      color: theme.palette.baseColors.deepSeaNavy.c75,
    },
    subheader: {
      whiteSpace: 'pre-line',
      color: theme.palette.baseColors.deepSeaNavy.c75,
    },
  };
});

const SmartReportName = () => {
  const classes = useStyles();
  const { report } = useEditSmartReport();
  const { setSnack } = useSnackBarContext();
  const [isEditing, setIsEditing] = useState(false);
  const [reportName, setReportName] = useState(report?.name || 'Untitled Report');

  const { updateReport, updatedReport, error, loading } = useUpdateReport();

  useEffect(() => {
    if (updatedReport?.id) {
      setSnack({
        message: 'Report name successfully updated.',
        severity: 'success',
        open: true,
      });
    }
  }, [updatedReport, setSnack]);

  useEffect(() => {
    if (error) {
      setSnack({
        message: 'Error updating report name. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [error, setSnack]);

  useEffect(() => {
    if (report) {
      setReportName(report.name);
    }
  }, [report]);

  const onNameBlur = () => {
    setIsEditing(false);
    if (report) {
      setReportName(report?.name);
    }
  };

  const onSubmit = e => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      const editedName = reportName.trim() || 'Untitled Report';
      updateReport({ ...report, name: editedName });
    }
  };

  return (
    <Grid item size='grow'>
      {isEditing ? (
        <Input
          className={classes.reportNameInput}
          type='text'
          autoFocus
          value={reportName}
          disabled={loading}
          onBlur={onNameBlur}
          onKeyDown={onSubmit}
          onChange={e => setReportName(e.target.value)}
        />
      ) : (
        <Typography
          className={classes.reportName}
          variant='h3'
          disabled={loading}
          onClick={() => setIsEditing(true)}
        >
          {reportName} <EditIcon />
        </Typography>
      )}
    </Grid>
  );
};

export const EditSmartReportHeader = () => {
  const classes = useStyles();

  const { id } = useParams();
  const history = useHistory();
  const { report, reportVersion, pages } = useEditSmartReport();
  const {
    publishReport,
    publishedReport,
    error: publishingError,
    loading: publishLoading,
  } = usePublishReport();
  const {
    updateReportVersion,
    updatedReportVersion,
    error: versionUpdateError,
    loading: versionUpdateLoading,
  } = useUpdateReportVersion();
  const { setSnack } = useSnackBarContext();
  const reportType = report?.smartReportType?.name || SmartReportTypeEnum.Asset;

  const onUpdateReportVersion = useCallback(() => {
    updateReportVersion({ ...reportVersion, pages });
  }, [reportVersion, pages, updateReportVersion]);

  useEffect(() => {
    if (publishedReport?.id) {
      setSnack({
        message: 'Report published successfully',
        severity: 'success',
        open: true,
      });

      // Redirect to the report list page
      history.push(ADMINS_SMART_REPORTS_PATH);
    }
  }, [history.push, publishedReport, setSnack]);

  useEffect(() => {
    if (publishingError) {
      setSnack({
        message: 'Error publishing report. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [publishingError, setSnack]);

  useEffect(() => {
    if (versionUpdateError) {
      setSnack({
        message: 'Error updating report version. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [versionUpdateError, setSnack]);

  useEffect(() => {
    if (updatedReportVersion?.id) {
      setSnack({
        message: 'Smart Report draft successfully saved.',
        severity: 'success',
        open: true,
      });
    }
  }, [updatedReportVersion, setSnack]);

  return (
    <>
      <Grid container justifyContent='space-between' sx={{ margin: '1em 0.5em 0 0.5em' }}>
        <Button
          variant='text'
          color='secondary'
          href={ADMINS_SMART_REPORTS_PATH}
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'start' }}
        >
          <Typography variant='body1' sx={{ color: 'secondary' }}>
            Back to reports
          </Typography>
        </Button>
        <Button
          variant='text'
          color='secondary'
          href={`${ADMINS_SMART_REPORTS_PATH}/${id}/preview`}
          startIcon={<RemoveRedEyeIcon />}
          sx={{ alignSelf: 'start' }}
        >
          <Typography variant='body1' sx={{ color: 'secondary' }}>
            View Report
          </Typography>
        </Button>
      </Grid>
      <Grid container justifyContent='space-between' sx={{ margin: '1em 0.5em 0 0.5em' }}>
        <SmartReportName />
        <Grid container item spacing={2}>
          <Button
            className={classes.saveButton}
            color='primary'
            variant='contained'
            onClick={onUpdateReportVersion}
            disabled={publishLoading || versionUpdateLoading}
          >
            Save Draft
          </Button>
          <Button
            className={classes.button}
            variant='contained'
            color='secondary'
            disabled={publishLoading || versionUpdateLoading}
            onClick={() => publishReport(report)}
          >
            Publish
          </Button>
        </Grid>
      </Grid>
      <Grid container alignItems='center' item sx={{ margin: '0 0.5em' }}>
        <Typography
          className={classes.subheader}
          variant='subtitle2'
          display='flex'
          alignItems='center'
        >
          {REPORT_TYPE_ICONS[reportType]}
          <span style={{ margin: '0 0.5em' }}>{reportType} Report</span>
          <Chip color='info' label={`v${reportVersion?.number}`} />
        </Typography>
      </Grid>
    </>
  );
};
